"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { ReactNode, useEffect } from "react";

import { SESSION_RECORDING_CONFIG } from "@/config/sessionRecordingConfig";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { initializeDeduplication } from "@/utils/eventDeduplication";
import { isLocalhostSync } from "@/utils/trackingFilter";

function PostHogProviderWrapper({ children }: { children: ReactNode }) {
  useSessionTracking();

  return <>{children}</>;
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    const skipTracking = isLocalhostSync();
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;

    // Initialize event deduplication
    initializeDeduplication({
      windowMs: 500,
      debug: process.env.NODE_ENV === 'development',
    });

    // Check if API key exists
    if (!posthogKey) {
      console.error('[PostHog] API key not configured. Tracking disabled.');
      return;
    }

    try {
      posthog.init(posthogKey, {
        api_host: "/ph",
        ui_host: "https://us.i.posthog.com",
        // Remove invalid 'defaults' property
        autocapture: !skipTracking,
        capture_pageview: skipTracking ? false : "history_change",
        capture_dead_clicks: !skipTracking,
        cross_subdomain_cookie: true,
        person_profiles: "identified_only",

        disable_session_recording: skipTracking,
        session_recording: skipTracking ? undefined : {
          sampleRate: SESSION_RECORDING_CONFIG.sampleRate,
          maskAllInputs: false,
          maskTextSelector: SESSION_RECORDING_CONFIG.maskSelectors.join(", "),
          // Simplify blockSelector to only password fields
          blockSelector: 'input[type="password"]',
        },

        enable_heatmaps: !skipTracking,
        enable_recording_console_log: !skipTracking,
      });

      console.log('[PostHog] Initialized successfully', {
        environment: skipTracking ? 'localhost' : 'production',
        sessionRecording: !skipTracking,
      });
    } catch (error) {
      console.error('[PostHog] Initialization failed:', error);
    }
  }, []);

  return (
    <PHProvider client={posthog}>
      <PostHogProviderWrapper>{children}</PostHogProviderWrapper>
    </PHProvider>
  );
}
